import { useEffect, useState } from "react";
import { Row, Col, Card, Container, Carousel } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useWallet } from "@solana/wallet-adapter-react";
import { Helmet } from "react-helmet";
import { GameGenre, HostType } from "../shared/enums";
import { useDarknetIdentity } from "../contexts/IdentityContext";
import StorageService from "../services/StorageService";
import ApiService from "../services/ApiService";
import { getGameAssetsUrl, formatDescription } from "../utilities/Helpers";
import CustomBreadcrumbs from "../components/common/CustomBreadcrumbs";
import OpenGamesWidgets from "../components/widgets/OpenGamesWidgets";

const GameInfo = () => {
    const navigate = useNavigate();
    const { gameId } = useParams();
    const [game, setGame] = useState<Game>();
    const { authenticated } = useDarknetIdentity();
    const { publicKey } = useWallet();
    const [gameAssetsUrl, setGameAssetsUrl] = useState<string>();

    useEffect(() => {
        ApiService.get('/games/' + gameId).then((response: any) => {
            if (!response) throw new Error("No response");
            if (!response.success) throw new Error("Failed to get game info");
            else setGame(response.data);
            setGameAssetsUrl(getGameAssetsUrl(Number(gameId)));
        }).catch((err: any) => {
            console.log(err);
        });
    }, [gameId]);

    async function handleCreateGame(event: any) {
        event.preventDefault();
        if (!authenticated) return;
        try {
            let response = await ApiService.post('/creategame');
            if (!response) throw new Error('No response');
            if (response.success) {
                StorageService.set('authToken', response.data.token);
                navigate(`/${game?.shortName}/${response.data.id}`);
            } else {
                console.error(response.message);
            }
        } catch (error: any) {
            console.error(error);
        }
    }

    function handleLaunchPopupGame(event: any) {
        event.preventDefault();
        if (!game) return;

        const url = game.website;
        const title = game.name;
        const w = 1280;
        const h = 720;

        const dualScreenLeft = window.screenLeft ?? window.screenX;
        const dualScreenTop = window.screenTop ?? window.screenY;

        const width = window.innerWidth ? window.innerWidth : document.documentElement.clientWidth;
        const height = window.innerHeight ? window.innerHeight : document.documentElement.clientHeight;

        const systemZoom = width / window.screen.availWidth;
        const left = (width - w) / 2 / systemZoom + dualScreenLeft
        const top = (height - h) / 2 / systemZoom + dualScreenTop

        window.open(url, title,
            `
            scrollbars=yes,
            width=${w / systemZoom}, 
            height=${h / systemZoom}, 
            top=${top}, 
            left=${left}
            `
        )
    }

    const LaunchGameButton = ({ play, pvp }: { play: boolean, pvp?: boolean }) => {
        if (!game) return <></>;
        if (!play) return <></>;
        let releaseDate = game.releaseDate ? new Date(game.releaseDate) : null;
        let now = new Date(Date.now());
        if (releaseDate && releaseDate > now && !game.released) return <button className="dg-btn dg-btn-secondary dg-btn-block text-light" disabled>Coming Soon!</button>;
        switch (game.hostType.name) {
            case HostType.Self:
            case HostType.Iframe:
                if (game.gameGenre.name === GameGenre.Wagered) {
                    if (authenticated && publicKey)
                        return <button className="dg-btn dg-btn-primary dg-btn-block text-light" onClick={handleCreateGame}>Launch Game</button>;
                    else
                        return <button className="dg-btn dg-btn-secondary dg-btn-block text-light" disabled>Account Required</button>;
                }
                return <button className="dg-btn dg-btn-primary dg-btn-block text-light" onClick={() => navigate(`/game/${game.id}/play`)}>Launch Game</button>;
            case HostType.Redirect:
                return <button className="dg-btn dg-btn-primary dg-btn-block text-light" onClick={handleLaunchPopupGame}>Launch Game</button>;
            case HostType.WebGL:
                return <a href={game.website} target="_blank" rel="noreferrer noopener" className="dg-btn dg-btn-primary dg-btn-block text-light">Launch Game</a>;
            case HostType.Download:
            default:
                return <>Not available</>;
        }
    }

    return (
        <>
            <Helmet>
                <style>{`
                    html {
                        background-image: url(${gameAssetsUrl}/background1.png);
                        background-repeat: repeat;
                        background-size: cover;
                    }
                `}</style>
                <title>{`Darknet Games | ${game?.name}`}</title>
            </Helmet>
            <Container>
                <Row>
                    <Col>
                        {game && (
                            <Card className="card-widget">
                                <Card.Header>
                                    <span className="d-none d-md-block">
                                        <CustomBreadcrumbs links={['home']} /> &gt; {game.publisher.name} &gt; {game.name}
                                    </span>
                                    <span className="d-block d-md-none">
                                        {game.publisher.name} &gt; {game.name}
                                    </span>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={4}>
                                            <Card.Text className="fs-7">
                                                <Row className="mb-3 text-center">
                                                    <Col className="d-none d-md-block">
                                                        <img src={`${gameAssetsUrl}/logo.png`} alt={game.name} height={96} />
                                                    </Col>
                                                    <Col className="d-block d-md-none">
                                                        <img src={`${gameAssetsUrl}/logo.png`} alt={game.name} width={256} />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col>
                                                        <div dangerouslySetInnerHTML={{ __html: `${formatDescription(game.description)}` }} />
                                                    </Col>
                                                </Row>
                                                <Row className="my-3">
                                                    <Col>
                                                        Publisher: <a href={game.publisher.website} target="_blank" rel="noreferrer noopener" title={game.publisher.name} >{game.publisher.name}</a>
                                                        <br />
                                                        Release date: {game.releaseDate ? new Date(game.releaseDate).toLocaleDateString() : 'TBD'}
                                                    </Col>
                                                </Row>
                                                <Row className="d-none d-md-block">
                                                    <Col>
                                                        {authenticated && publicKey && (
                                                            <LaunchGameButton play={(game.id !== 1)} />
                                                        )}
                                                    </Col>
                                                </Row>
                                            </Card.Text>
                                        </Col>
                                        <Col md={8} className="text-center">
                                            <Carousel>
                                                <Carousel.Item>
                                                    <img src={`${gameAssetsUrl}/screenshot1.png`} alt="Screenshot 1" className="w-100" />
                                                </Carousel.Item>
                                                <Carousel.Item>
                                                    <img src={`${gameAssetsUrl}/screenshot2.png`} alt="Screenshot 2" className="w-100" />
                                                </Carousel.Item>
                                                <Carousel.Item>
                                                    <img src={`${gameAssetsUrl}/screenshot3.png`} alt="Screenshot 3" className="w-100" />
                                                </Carousel.Item>
                                            </Carousel>
                                        </Col>
                                    </Row>
                                </Card.Body>
                                {game.hostType.name === HostType.Redirect && (
                                    <Card.Footer className="d-none d-md-block">
                                        <span className="fs-8">
                                            NOTICE: This game uses a popup interface and may be blocked by your adblocker. If you are having trouble
                                            launching the game, please disable your adblocker for this site. Be sure to resize your browser window
                                            after launching the game to ensure the game is completely visible on your screen.
                                        </span>
                                    </Card.Footer>
                                )}
                            </Card>
                        )}
                    </Col>
                </Row>
                {game && game.id === 1 && (
                    <Row className="mt-3">
                        <Col>
                            <OpenGamesWidgets />
                        </Col>
                    </Row>
                )}
            </Container>
        </>
    )
}

export default GameInfo;